import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { IntroReveal } from "@/components/IntroReveal";
import { AuthCallback } from "@/components/AuthCallback";
import { ChatWidget } from "@/components/ChatWidget";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";

const Work = lazy(() => import("@/pages/Work"));
const Services = lazy(() => import("@/pages/Services"));
const Process = lazy(() => import("@/pages/Process"));
const About = lazy(() => import("@/pages/About"));
const Insights = lazy(() => import("@/pages/Insights"));
const InsightsArticle = lazy(() => import("@/pages/InsightsArticle"));
const Contact = lazy(() => import("@/pages/Contact"));
const Admin = lazy(() => import("@/pages/Admin"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const useLenis = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(hover: none), (pointer: coarse)").matches) return;
    let lenis = null;
    let raf = null;
    let alive = true;
    (async () => {
      const { default: Lenis } = await import("lenis");
      if (!alive) return;
      lenis = new Lenis({ lerp: 0.09 });
      window.__lenis = lenis;
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    })();
    return () => {
      alive = false;
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/work" element={<Work />} />
        <Route path="/services" element={<Services />} />
        <Route path="/process" element={<Process />} />
        <Route path="/about" element={<About />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<InsightsArticle />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  useLenis();
  const [chatReady, setChatReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setChatReady(true), 1800);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <IntroReveal />
        {chatReady && <ChatWidget />}
        <Nav />
        <AppRouter />
        <Footer />
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
